import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';

const inputClasses =
  'mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm transition-colors';

const selectClasses =
  'mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm transition-colors';

const labelClasses = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

export interface EnvironmentFormData {
  name: string;
  description: string;
  environment_type: string;
  endpoint_url: string;
  http_method: string;
  request_body: string;
  content_type: string;
  monitoring_interval_minutes: number;
  timeout_seconds: number;
  expected_status_code: number;
  alert_on_failure: boolean;
  consecutive_failures_threshold: number;
  is_active: boolean;
  client_id: string | null;
}

export interface Environment extends Partial<EnvironmentFormData> {
  id?: string;
}

interface CreateEditEnvironmentModalProps {
  environment: Environment | null;
  onSave: (formData: EnvironmentFormData) => void;
  onClose: () => void;
}

export default function CreateEditEnvironmentModal({ environment, onSave, onClose }: CreateEditEnvironmentModalProps) {
  const { user } = useAuth();
  const { selectedClientId, accessibleClients } = useClient();
  const isClientUser = Boolean(user?.client_id);

  // Get the selected client object from the list of accessible clients
  const selectedClient = accessibleClients.find(c => c.id === selectedClientId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    environment_type: 'production',
    endpoint_url: '',
    http_method: 'GET',
    request_body: '',
    content_type: 'application/json',
    monitoring_interval_minutes: 5,
    timeout_seconds: 30,
    expected_status_code: 200,
    alert_on_failure: true,
    consecutive_failures_threshold: 3,
    is_active: true,
    client_id: selectedClientId || user?.client_id || null,
  });

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (environment) {
      setFormData({
        name: environment.name || '',
        description: environment.description || '',
        environment_type: environment.environment_type || 'production',
        endpoint_url: environment.endpoint_url || '',
        http_method: environment.http_method || 'GET',
        request_body: environment.request_body || '',
        content_type: environment.content_type || 'application/json',
        monitoring_interval_minutes: environment.monitoring_interval_minutes || 5,
        timeout_seconds: environment.timeout_seconds || 30,
        expected_status_code: environment.expected_status_code || 200,
        alert_on_failure: environment.alert_on_failure !== false,
        consecutive_failures_threshold: environment.consecutive_failures_threshold || 3,
        is_active: environment.is_active !== false,
        client_id: environment.client_id ?? null,
      });
    }
  }, [environment]);

  // Update client_id when selectedClientId changes (for new environments only)
  useEffect(() => {
    if (!environment) {
      setFormData((prev) => ({
        ...prev,
        client_id: selectedClientId || user?.client_id || null,
      }));
    }
  }, [selectedClientId, user?.client_id, environment]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;
    let fieldValue: string | number | boolean;
    if (type === 'checkbox') {
      fieldValue = checked;
    } else if (type === 'number') {
      fieldValue = Number.parseInt(value);
    } else {
      fieldValue = value;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.endpoint_url.trim()) {
      if (!/^https?:\/\/.+/.test(formData.endpoint_url)) {
        newErrors.endpoint_url = 'Must be a valid HTTP/HTTPS URL';
      }
    } else {
      newErrors.endpoint_url = 'Endpoint URL is required';
    }

    if (formData.monitoring_interval_minutes < 1 || formData.monitoring_interval_minutes > 60) {
      newErrors.monitoring_interval_minutes = 'Must be between 1 and 60 minutes';
    }

    if (formData.timeout_seconds < 5 || formData.timeout_seconds > 300) {
      newErrors.timeout_seconds = 'Must be between 5 and 300 seconds';
    }

    if (formData.consecutive_failures_threshold < 1) {
      newErrors.consecutive_failures_threshold = 'Must be at least 1';
    }

    // Validate client_id for non-client users
    if (!isClientUser && !formData.client_id) {
      newErrors.client_id = 'Please select a client before creating an environment';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
          onClick={onClose} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClose(); }} role="presentation"
        />


        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {environment ? 'Edit Endpoint' : 'Add New Endpoint'}
              </h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-5 max-h-[calc(100vh-220px)] overflow-y-auto">
              {/* Client Info - Show for non-client users */}
              {!isClientUser && (
                <div className={`rounded-lg p-4 ${
                  formData.client_id
                    ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      formData.client_id
                        ? 'bg-blue-100 dark:bg-blue-800/40'
                        : 'bg-yellow-100 dark:bg-yellow-800/40'
                    }`}>
                      {formData.client_id ? (
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {formData.client_id ? (
                        <>
                          <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                            {selectedClient?.name || 'Client Selected'}
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                            This endpoint will be created for this client
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-yellow-900 dark:text-yellow-200">
                            No Client Selected
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-0.5">
                            Please select a client from the top navigation
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  {errors.client_id && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.client_id}</p>}
                </div>
              )}

              {/* Name */}
              <div>
                <label htmlFor="env-modal-name" className={labelClasses}>Name *</label>
                <input
                                                                                  id="env-modal-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="e.g., Production - US East"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="env-modal-description" className={labelClasses}>Description</label>
                <textarea
                                                                                  id="env-modal-description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className={inputClasses}
                  placeholder="Optional description"
                />
              </div>

              {/* Environment Type & Endpoint URL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="env-modal-type" className={labelClasses}>Environment Type *</label>
                  <select
                                                                                  id="env-modal-type"
                    name="environment_type"
                    value={formData.environment_type}
                    onChange={handleChange}
                    className={selectClasses}
                  >
                    <option value="production">Production</option>
                    <option value="staging">Staging</option>
                    <option value="development">Development</option>
                    <option value="qa">QA</option>
                    <option value="uat">UAT</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="env-modal-http-method" className={labelClasses}>HTTP Method *</label>
                  <select
                                                                                  id="env-modal-http-method"
                    name="http_method"
                    value={formData.http_method}
                    onChange={handleChange}
                    className={selectClasses}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="PATCH">PATCH</option>
                    <option value="DELETE">DELETE</option>
                    <option value="HEAD">HEAD</option>
                    <option value="OPTIONS">OPTIONS</option>
                  </select>
                </div>
              </div>

              {/* Endpoint URL - full width */}
              <div>
                <label htmlFor="env-modal-endpoint-url" className={labelClasses}>Endpoint URL *</label>
                <input
                                                                                  id="env-modal-endpoint-url"
                  type="text"
                  name="endpoint_url"
                  value={formData.endpoint_url}
                  onChange={handleChange}
                  className={inputClasses}
                  placeholder="https://api.example.com/health"
                />
                {errors.endpoint_url && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.endpoint_url}</p>}
              </div>

              {/* Content Type */}
              <div>
                <label htmlFor="env-modal-content-type" className={labelClasses}>Content Type</label>
                <select
                                                                                  id="env-modal-content-type"
                  name="content_type"
                  value={formData.content_type}
                  onChange={handleChange}
                  className={selectClasses}
                >
                  <option value="application/json">application/json</option>
                  <option value="application/x-www-form-urlencoded">application/x-www-form-urlencoded</option>
                  <option value="multipart/form-data">multipart/form-data</option>
                  <option value="text/plain">text/plain</option>
                  <option value="application/xml">application/xml</option>
                </select>
              </div>

              {/* Request Body - Only show for POST, PUT, PATCH */}
              {['POST', 'PUT', 'PATCH'].includes(formData.http_method) && (
                <div>
                  <label htmlFor="env-modal-request-body" className={labelClasses}>Request Body</label>
                  <textarea
                                                                                  id="env-modal-request-body"
                    name="request_body"
                    value={formData.request_body}
                    onChange={handleChange}
                    rows={4}
                    className={`${inputClasses} font-mono`}
                    placeholder='{"key": "value"}'
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Optional request body to send with the health check
                  </p>
                </div>
              )}

              {/* Monitoring Configuration */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Monitoring Configuration
                </h4>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="env-modal-interval" className={labelClasses}>Interval (min) *</label>
                    <input
                                                                                  id="env-modal-interval"
                      type="number"
                      name="monitoring_interval_minutes"
                      value={formData.monitoring_interval_minutes}
                      onChange={handleChange}
                      min="1"
                      max="60"
                      className={inputClasses}
                    />
                    {errors.monitoring_interval_minutes && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.monitoring_interval_minutes}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="env-modal-timeout" className={labelClasses}>Timeout (sec) *</label>
                    <input
                                                                                  id="env-modal-timeout"
                      type="number"
                      name="timeout_seconds"
                      value={formData.timeout_seconds}
                      onChange={handleChange}
                      min="5"
                      max="300"
                      className={inputClasses}
                    />
                    {errors.timeout_seconds && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.timeout_seconds}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="env-modal-expected-status" className={labelClasses}>Expected Status *</label>
                    <input
                                                                                  id="env-modal-expected-status"
                      type="number"
                      name="expected_status_code"
                      value={formData.expected_status_code}
                      onChange={handleChange}
                      className={inputClasses}
                    />
                  </div>
                </div>
              </div>

              {/* Alert Configuration */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-5">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                  Alert Configuration
                </h4>

                <div className="space-y-4">
                  <span className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="alert_on_failure"
                      checked={formData.alert_on_failure}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Send alerts on failure
                    </span>
                  </span>

                  <div>
                    <label htmlFor="env-modal-failure-threshold" className={labelClasses}>Consecutive Failures Threshold *</label>
                    <input
                                                                                  id="env-modal-failure-threshold"
                      type="number"
                      name="consecutive_failures_threshold"
                      value={formData.consecutive_failures_threshold}
                      onChange={handleChange}
                      min="1"
                      className={inputClasses}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Number of consecutive failures before marking as down
                    </p>
                    {errors.consecutive_failures_threshold && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.consecutive_failures_threshold}</p>
                    )}
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Active (monitoring enabled)
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                {environment ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
